/*
 * Render SchemaTagInputsWithDefault for a new bioentity form.
 */
import {gql} from '@apollo/client';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {TagSchema} from 'constants/RegistryConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  CreateBioentityTagInputsQuery,
  CreateBioentityTagInputsQueryVariables,
} from 'registry/create-bioentity/__generated/CreateBioentityTagInputsQuery';
import SchemaTagInputsWithDefault from 'schemas/tags/SchemaTagInputsWithDefault';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {Optional} from 'util/Types';

export function useCreateBioentityTagInputs(): [
  Optional<TagSchema>,
  Array<SchemaTag>,
  {onChange: (tagSchema: {id: string} | null) => void; selectedTagSchemaId?: string | null},
  CreateBioentityTagInputProps,
] {
  const [tagSchema, setTagSchema] = useState<Optional<TagSchema>>(null);
  const [tags, setTags] = useState<Array<SchemaTag>>([]);
  const schemaSelectorProps = {
    onChange: (e: TagSchema | null) => {
      setTagSchema(e);
    },
    selectedTagSchemaId: tagSchema?.id,
  };
  const tagInputsProps = {labelStyle: LabelStyle.LABEL, onChange: setTags, tagSchemaId: tagSchema?.id, tags};
  return [tagSchema, tags, schemaSelectorProps, tagInputsProps];
}

export interface CreateBioentityTagInputProps {
  labelStyle: LabelStyle;
  onChange: (tags: Array<SchemaTag>) => void;
  tags: Array<SchemaTag>;

  // Used by connect
  tagSchemaId?: string | null;
}

interface ConnectedProps {
  tagSchema: TagSchema | null;
}

function CreateBioentityTagInputs({
  labelStyle,
  onChange,
  tagSchema,
  tags,
}: CreateBioentityTagInputProps & ConnectedProps): JSX.Element {
  return (
    <SchemaTagInputsWithDefault labelStyle={labelStyle} onChange={onChange} schema={tagSchema} tags={tags} />
  );
}

export const CREATE_BIOENTITY_TAG_INPUTS_QUERY = gql`
  query CreateBioentityTagInputsQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    tagSchema(id: $tagSchemaId) {
      ...TagSchemaGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingCreateBioentityTagInputs(props: CreateBioentityTagInputProps): JSX.Element {
  const Loading = BenchlingQuery.DEFAULT_RENDER_LOADING;
  return (
    <BenchlingQuery<CreateBioentityTagInputsQuery, CreateBioentityTagInputsQueryVariables>
      query={CREATE_BIOENTITY_TAG_INPUTS_QUERY}
      renderLoading={Loading}
      skip={!props.tagSchemaId}
      variables={{tagSchemaId: props.tagSchemaId ?? ''}}
    >
      {(data) => {
        const schema = data ? data.tagSchema : null;
        assert(
          schema == null || !isInaccessible(schema),
          `Expecting a TagSchema, but it was inaccessible (tagSchemaId=${props.tagSchemaId})`
        );
        return (
          <CreateBioentityTagInputs
            {...props}
            tagSchema={schema ? graphQLToREST('TagSchema', schema) : null}
          />
        );
      }}
    </BenchlingQuery>
  );
}
